import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, X } from 'lucide-react';
import { Project } from '@/types/product-manager';
import { createTask } from '@/services/productManagerService';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { getAllUserAppAccess, AVAILABLE_APPS } from '@/services/appAccess';
import { productManagerEmailService } from '@/services/productManagerEmailService';
import ImageUpload from './ImageUpload';
import ImageGallery from './ImageGallery';
import { ImageUploadResult } from '@/services/productManagerImageService';
import { useModalClipboardPaste } from '@/hooks/useModalClipboardPaste';

interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskCreated: () => void;
  projects: Project[];
  defaultProjectId?: string;
}

const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  open,
  onOpenChange,
  onTaskCreated,
  projects,
  defaultProjectId
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [availableAssignees, setAvailableAssignees] = useState<string[]>([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [uploadedImages, setUploadedImages] = useState<ImageUploadResult[]>([]);
  const [imageUploadError, setImageUploadError] = useState<string>('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: defaultProjectId || '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    assignedTo: '',
    dueDate: null as Date | null,
    tags: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');

  // Load users with Product Manager access
  useEffect(() => {
    const loadProductManagerUsers = async () => {
      try {
        const allUserAccess = await getAllUserAppAccess();
        const productManagerUsers = allUserAccess
          .filter(userAccess => userAccess.allowedApps.includes(AVAILABLE_APPS.ProductManager))
          .map(userAccess => userAccess.email);
        setAvailableAssignees(productManagerUsers);
      } catch (error) {
        console.error('Error loading Product Manager users:', error);
        setAvailableAssignees([]);
      }
    };

    if (open) {
      loadProductManagerUsers();
    }
  }, [open]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Task name is required';
    }
    
    if (!formData.projectId) {
      newErrors.projectId = 'Please select a project';
    }
    
    if (!formData.assignedTo) {
      newErrors.assignedTo = 'Please assign the task to a user';
    }
    
    if (!formData.dueDate) {
      newErrors.dueDate = 'Please set a due date';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email) return;

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const now = Timestamp.now();
      
      // Build task object without undefined values
      const task: any = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        projectId: formData.projectId,
        status: 'todo',
        priority: formData.priority,
        createdBy: user.email,
        createdAt: now,
        updatedAt: now
      };
      
      // Only add assignedTo if it has a value
      if (formData.assignedTo) {
        task.assignedTo = formData.assignedTo;
      }
      
      // Only add dueDate if it's set
      if (formData.dueDate) {
        task.dueDate = Timestamp.fromDate(formData.dueDate);
      }
      
      // Only add tags if there are any
      if (formData.tags.length > 0) {
        task.tags = formData.tags;
      }

      // Only add images if there are any
      if (uploadedImages.length > 0) {
        task.images = uploadedImages.map(img => img.url);
      }

      const createdTaskId = await createTask(task);
      
      // Send email notification if task is assigned to someone
      if (formData.assignedTo) {
        try {
          const selectedProject = projects.find(p => p.id === formData.projectId);
          await productManagerEmailService.sendTaskAssignedNotification(
            formData.assignedTo,
            createdTaskId,
            formData.title,
            selectedProject?.name || 'Unknown Project',
            user.email
          );
        } catch (emailError) {
          console.error('Error sending email notification:', emailError);
          // Don't fail the task creation if email fails
        }
      }
      
      // Close dialog first to prevent focus issues
      onOpenChange(false);
      // Then trigger data reload and reset
      onTaskCreated();
      handleReset();
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      projectId: defaultProjectId || '',
      priority: 'medium',
      assignedTo: '',
      dueDate: null,
      tags: []
    });
    setTagInput('');
    setErrors({});
    setUploadedImages([]);
    setImageUploadError('');
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    console.log('Date selected:', date); // Debug log
    setFormData(prev => ({
      ...prev,
      dueDate: date || null
    }));
    // Close the popover after date selection
    setIsDatePickerOpen(false);
  };

  const handleImagesUploaded = (images: ImageUploadResult[]) => {
    setUploadedImages(prev => [...prev, ...images]);
    setImageUploadError('');
  };

  const handleImageUploadError = (error: string) => {
    setImageUploadError(error);
  };

  const handleImageDeleted = (imageUrl: string) => {
    setUploadedImages(prev => prev.filter(img => img.url !== imageUrl));
  };

  // Handle clipboard paste - convert File[] to ImageUploadResult[] format
  const handleClipboardPaste = async (files: File[]) => {
    try {
      // Import the upload function
      const { uploadTaskImage } = await import('@/services/productManagerImageService');
      
      // Upload each file and convert to ImageUploadResult format
      const uploadPromises = files.map(async (file) => {
        const result = await uploadTaskImage('temp', file);
        return result;
      });
      
      const uploadResults = await Promise.all(uploadPromises);
      handleImagesUploaded(uploadResults);
    } catch (error) {
      console.error('Error uploading clipboard images:', error);
      handleImageUploadError('Failed to upload clipboard images');
    }
  };

  // Modal-level clipboard paste functionality
  const { modalRef } = useModalClipboardPaste({
    onImagesPasted: handleClipboardPaste,
    onError: handleImageUploadError,
    maxImages: 3,
    disabled: loading,
    enablePaste: open
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={modalRef} className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 z-50" style={{ zIndex: 9999 }}>
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription>
            Create a new task and assign it to a project.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter task title"
              required
              className={`bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 ${
                errors.title ? 'border-red-500 dark:border-red-500' : ''
              }`}
            />
            {errors.title && (
              <p className="text-sm text-red-500">{errors.title}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the task"
              rows={3}
              className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="project">Project *</Label>
              <Select
                value={formData.projectId}
                onValueChange={(value) => setFormData(prev => ({ ...prev, projectId: value }))}
                required
              >
                <SelectTrigger className={`bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 ${
                  errors.projectId ? 'border-red-500 dark:border-red-500' : ''
                }`}>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.projectId && (
                <p className="text-sm text-red-500">{errors.projectId}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: 'low' | 'medium' | 'high' | 'critical') =>
                  setFormData(prev => ({ ...prev, priority: value }))
                }
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assignee">Assign To *</Label>
              <Select
                value={formData.assignedTo}
                onValueChange={(value) => setFormData(prev => ({ ...prev, assignedTo: value }))}
              >
                <SelectTrigger className={`bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 ${
                  errors.assignedTo ? 'border-red-500 dark:border-red-500' : ''
                }`}>
                  <SelectValue placeholder="Assign To" />
                </SelectTrigger>
                <SelectContent>
                  {availableAssignees.map(assignee => (
                    <SelectItem key={assignee} value={assignee}>
                      {assignee}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.assignedTo && (
                <p className="text-sm text-red-500">{errors.assignedTo}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
                      !formData.dueDate && "text-muted-foreground",
                      errors.dueDate && "border-red-500 dark:border-red-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.dueDate ? format(formData.dueDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 z-50"
                  align="start"
                  style={{ zIndex: 9999 }}
                >
                  <Calendar
                    mode="single"
                    selected={formData.dueDate || undefined}
                    onSelect={handleDateSelect}
                    initialFocus
                    className="bg-white dark:bg-gray-800 pointer-events-auto [&_button:hover]:!bg-gray-100 [&_button:hover]:dark:!bg-gray-700 [&_button:hover]:!text-gray-900 [&_button:hover]:dark:!text-gray-100"
                    style={{ pointerEvents: 'auto' }}
                  />
                </PopoverContent>
              </Popover>
              {errors.dueDate && (
                <p className="text-sm text-red-500">{errors.dueDate}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <div className="flex gap-2">
              <Input
                id="tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                placeholder="Add tags (press Enter)"
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                Add
              </Button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Image Upload Section */}
          <div className="space-y-2">
            <Label>Images</Label>
            <ImageUpload
              entityType="task"
              entityId="temp" // Will be replaced with actual task ID after creation
              onImagesUploaded={handleImagesUploaded}
              onError={handleImageUploadError}
              disabled={loading}
              maxImages={3}
              enableClipboardPaste={true}
            />
            
            {imageUploadError && (
              <p className="text-sm text-red-500">{imageUploadError}</p>
            )}
            
            {uploadedImages.length > 0 && (
              <ImageGallery
                images={uploadedImages.map(img => img.url)}
                onImageDeleted={handleImageDeleted}
                canDelete={true}
                showDownload={false}
              />
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                handleReset();
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaskDialog;